import { Box, Button, HStack, Text } from '@chakra-ui/react';
import { AnimatePresence, motion } from 'framer-motion';
import { FC, useEffect, useState } from 'react';
import { useIsMobile } from '~/common/hooks/useIsMobile';
import { ChatTriggerIcon } from './ChatTriggerIcon';

const MotionBox = motion(Box);

const textVariants = {
  hidden: { opacity: 0, y: 15 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { type: 'spring', stiffness: 100, damping: 15, duration: 0.4 },
  },
  exit: {
    opacity: 0,
    y: -15,
    transition: { duration: 0.2 },
  },
};

const allSuggestions = [
  'Need help with this listing...',
  'Want to analyze the real-time market data...',
  'What are the key selling points?',
  'How to make this listing stand out to buyers?',
  'Make the case for my seller to reduce price with market data...',
];

const INTERVAL_MS = 5_000;

interface ListingCopilotSuggestionsProps {
  onSuggestionClick: (prompt?: string) => void;
}

export const ListingCopilotSuggestions: FC<ListingCopilotSuggestionsProps> = ({
  onSuggestionClick,
}) => {
  const isMobile = useIsMobile();
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (allSuggestions.length <= 1) return;

    const intervalId = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % allSuggestions.length);
    }, INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, []);

  return (
    <Box
      _hover={{
        bgGradient:
          'linear(to-b, rgba(105, 99, 196, 0.4), rgba(105, 99, 196, 0.2))',
        borderColor: 'whiteAlpha.200',
      }}
      alignItems="center"
      bgGradient="linear(to-b, rgba(105, 99, 196, 0.3), rgba(105, 99, 196, 0.1))"
      border="1px solid"
      borderColor="whiteAlpha.100"
      borderRadius="full"
      cursor="pointer"
      display="flex"
      height="50px"
      p="8px 16px"
      transition="all 0.2s ease"
      onClick={() => {
        onSuggestionClick();
      }}
    >
      <HStack
        alignItems="center"
        height="100%"
        justifyContent="space-between"
        spacing={4}
        width="100%"
      >
        <HStack flex={1} overflow="hidden" spacing={3}>
          <ChatTriggerIcon />
          <Box height="24px" position="relative" width="100%">
            <AnimatePresence>
              <MotionBox
                key={currentIndex}
                animate="visible"
                aria-live="polite"
                exit="exit"
                initial="hidden"
                position="absolute"
                role="status"
                variants={textVariants}
                width="100%"
              >
                <Text color="whiteAlpha.800" fontWeight="medium" noOfLines={1}>
                  {allSuggestions[currentIndex]}
                </Text>
              </MotionBox>
            </AnimatePresence>
          </Box>
        </HStack>

        {!isMobile && (
          <HStack flexShrink={0} spacing={2}>
            <Button
              _hover={{ bg: 'whiteAlpha.300' }}
              bg="whiteAlpha.200"
              borderRadius="full"
              color="white"
              px={4}
              size="xs"
              onClick={(e) => {
                e.stopPropagation();
                onSuggestionClick(
                  'Help me make this listing stand out to buyers with provided market data'
                );
              }}
            >
              Make this listing stand out
            </Button>
            <Button
              _hover={{ bg: 'whiteAlpha.300' }}
              bg="whiteAlpha.200"
              borderRadius="full"
              color="white"
              px={4}
              size="xs"
              onClick={(e) => {
                e.stopPropagation();
                onSuggestionClick(
                  'Help me make a strong offer with provided market data'
                );
              }}
            >
              Make competitive offer
            </Button>
          </HStack>
        )}
      </HStack>
    </Box>
  );
};
